import React, {
  createContext,
  use,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import * as Sentry from "@sentry/react";

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * The authoritative permission + probe state for the app.
 *
 * - `idle`        — `hasCompletedFlow` is false; no probe attempted yet.
 *                   First-time users stay here until they complete onboarding.
 * - `verifying`   — Probe or `requestPermission` call is in flight.
 * - `granted`     — Probe or `requestPermission` confirmed access.
 * - `denied`      — PERMISSION_DENIED from the browser or OS; user action required.
 * - `unavailable` — TIMEOUT or POSITION_UNAVAILABLE; permission likely granted but
 *                   GPS/network signal is lost. The watcher remains active and will
 *                   recover to `granted` automatically when signal returns.
 */
export type LocationStatus =
  | "idle"
  | "verifying"
  | "granted"
  | "denied"
  | "unavailable";

export interface UseLocationPermissionReturn {
  /** Current permission + probe state. */
  status: LocationStatus;
  /**
   * True once the user has completed the location onboarding flow at least
   * once. Persisted in localStorage — the only value the hook stores.
   * Used to gate the iOS probe (safe to call only after first consent) and
   * to control LocationStatusBar visibility.
   */
  hasCompletedFlow: boolean;
  /**
   * Latest coordinates from the active `watchPosition` watcher.
   * In-memory only — not persisted to localStorage.
   * Null while verifying, or if permission is denied/idle.
   */
  position: GeolocationCoordinates | null;
  /** Human-readable error from the last failed geolocation call. */
  error: string | null;
  /**
   * Triggers the OS permission dialog and starts the `watchPosition` watcher
   * on success. Called by `LocationLandingPage` when the user taps
   * "Use My Location" for the first time.
   * Resolves `true` if permission was granted, `false` if denied.
   */
  requestPermission: () => Promise<boolean>;
  /**
   * Clears `hasCompletedFlow` from localStorage, stops the `watchPosition`
   * watcher, and resets all state to `idle`.
   * Called by the "Disable location" button in `LocationSettingsSheet`.
   */
  resetPermissionState: () => void;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

const DEFAULT_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  timeout: 10_000,
  maximumAge: 30_000,
};

interface LocationPermissionProviderProps {
  children: React.ReactNode;
  /**
   * Passed to both the iOS probe (`getCurrentPosition`) and the continuous
   * watcher (`watchPosition`). Defaults to battery-friendly values appropriate
   * for transit use (network location, 10s timeout, 30s position cache).
   */
  options?: PositionOptions;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const LocationPermissionContext = createContext<
  UseLocationPermissionReturn | undefined
>(undefined);

// ─── Storage ──────────────────────────────────────────────────────────────────

const STORAGE_KEY = "next-stop:location-permission";

function readHasCompletedFlow(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { hasCompletedFlow?: boolean };
    return parsed.hasCompletedFlow === true;
  } catch {
    return false;
  }
}

function writeHasCompletedFlow(value: boolean): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ hasCompletedFlow: value, version: 2 }),
    );
  } catch (err) {
    Sentry.captureException(err, { level: "warning" });
  }
}

function clearStorage(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage unavailable
  }
}

// ─── Provider implementation ──────────────────────────────────────────────────

export function LocationPermissionProvider({
  children,
  options = DEFAULT_OPTIONS,
}: LocationPermissionProviderProps) {
  const hasCompletedFlow = readHasCompletedFlow();
  const [status, setStatus] = useState<LocationStatus>(
    hasCompletedFlow ? "verifying" : "idle",
  );
  const [position, setPosition] = useState<GeolocationCoordinates | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [completedFlow, setCompletedFlow] = useState(hasCompletedFlow);

  const watchIdRef = useRef<number | null>(null);

  const stopWatch = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  }, []);

  const startWatch = useCallback(() => {
    stopWatch();
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setPosition(pos.coords);
        setStatus("granted");
        setError(null);
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          setStatus("denied");
          setPosition(null);
        } else {
          // TIMEOUT / POSITION_UNAVAILABLE — permission likely granted but signal
          // lost. Keep last known position; watcher recovers to "granted" when
          // signal returns. Moving out of "verifying" prevents the status bar
          // from staying hidden indefinitely during the initial probe.
          setStatus("unavailable");
        }
      },
      options,
    );
  }, [options, stopWatch]);

  // On mount: probe via Permissions API (desktop) or watchPosition (iOS).
  // Only runs when hasCompletedFlow is true — never probes before first consent.
  useEffect(() => {
    if (!completedFlow) return;

    const probe = async () => {
      // Desktop: Permissions API resolves synchronously-ish
      if ("permissions" in navigator) {
        try {
          const result = await navigator.permissions.query({
            name: "geolocation",
          });
          if (result.state === "denied") {
            setStatus("denied");
            return;
          }
          if (result.state === "granted") {
            startWatch();
            return;
          }
          // "prompt" — iOS always returns this; fall through to watchPosition probe
        } catch {
          // Permissions API unavailable — fall through
        }
      }
      // iOS / Permissions API unavailable: watchPosition discovers real state
      startWatch();
    };

    void probe();

    return () => {
      stopWatch();
    };
  }, [completedFlow, startWatch, stopWatch]);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    setStatus("verifying");
    setError(null);

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setPosition(pos.coords);
          setCompletedFlow(true);
          writeHasCompletedFlow(true);
          startWatch();
          setStatus("granted");
          resolve(true);
        },
        (err) => {
          let msg = "Could not get your location.";
          if (err.code === err.PERMISSION_DENIED) {
            msg =
              "Location access denied. Please enable location in your browser settings.";
            setStatus("denied");
          } else {
            // TIMEOUT / POSITION_UNAVAILABLE — permission likely granted but no
            // signal yet. Mark unavailable so the onboarding screen unblocks.
            if (err.code === err.POSITION_UNAVAILABLE)
              msg = "Location information unavailable.";
            else if (err.code === err.TIMEOUT)
              msg = "Location request timed out.";
            setStatus("unavailable");
          }
          setCompletedFlow(true);
          writeHasCompletedFlow(true);
          setError(msg);
          resolve(false);
        },
        options,
      );
    });
  }, [options, startWatch]);

  const resetPermissionState = useCallback(() => {
    stopWatch();
    clearStorage();
    setCompletedFlow(false);
    setStatus("idle");
    setPosition(null);
    setError(null);
  }, [stopWatch]);

  return (
    // eslint-disable-next-line react-x/no-context-provider
    <LocationPermissionContext.Provider
      value={{
        status,
        hasCompletedFlow: completedFlow,
        position,
        error,
        requestPermission,
        resetPermissionState,
      }}
    >
      {children}
    </LocationPermissionContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Consumes the nearest {@link LocationPermissionProvider}.
 * Must be rendered inside one — throws if called outside the tree.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useLocationPermission(): UseLocationPermissionReturn {
  const ctx = use(LocationPermissionContext);
  if (!ctx) {
    throw new Error(
      "useLocationPermission must be used inside <LocationPermissionProvider>",
    );
  }
  return ctx;
}
